import { useState, useEffect } from 'react';
import { FaDownload, FaCalendarAlt } from 'react-icons/fa';
import { enqueueSnackbar } from 'notistack';
import Select from '../../../components/Select/Select';
import Input from '../../../components/Input/Input';
import Button from '../../../components/Button/Button';
import { downloadAttendanceReport, getAttendanceLabelList } from '../../../services/AppManagement/AttendanceService';
import './AttendanceAllClassesDownload.css';

// Fallback only, used until the real label list loads (or if it fails to
// load) — the actual options always come from getAttendanceLabelList so this
// filter mirrors the exact set of statuses available when marking attendance
// (AttendanceManage.js's own fetchAttendanceLabelList), not a hardcoded pair.
const FALLBACK_STATUS_OPTIONS = [
  { id: 'present', name: 'Present' },
  { id: 'absent', name: 'Absent' },
];

const today = new Date().toISOString().split('T')[0];

// All-classes attendance report for a single date, optionally filtered to
// one status — separate from the per-class date-range report below
// (AttendanceReport.js's own filter), since this one always spans every
// class and only ever covers one day. Confirmed by the user (2026-10-29):
// GET attendance-report/download?date=...&status=... (status omitted = all).
const AttendanceAllClassesDownload = () => {
  const [date, setDate] = useState('');
  const [status, setStatus] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [statusOptions, setStatusOptions] = useState(FALLBACK_STATUS_OPTIONS);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const response = await getAttendanceLabelList();
        if (response.status === 200) {
          const labels = (response.data.data || [])
            .filter((item) => item.label_name && item.label_name !== 'Not Marked')
            .map((item) => ({ id: item.label_name.toLowerCase(), name: item.label_name }));
          if (labels.length > 0) {
            setStatusOptions(labels);
          }
        }
      } catch (error) {
        console.error('Error fetching attendance label list:', error);
      }
    };
    fetchLabels();
  }, []);

  const handleDownload = async () => {
    if (!date) {
      enqueueSnackbar('Please select a date.', { variant: 'error' });
      return;
    }
    setIsDownloading(true);
    try {
      const response = await downloadAttendanceReport(date, status || undefined);
      const contentType =
        response.headers?.['content-type'] ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance-report-${date}${status ? `-${status}` : ''}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar('Failed to download attendance report.', { variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="attendance-download-card">
      <div className="attendance-download-heading">
        <span className="attendance-download-icon"><FaCalendarAlt /></span>
        <div>
          <p className="attendance-download-title">All Classes Attendance Report</p>
          <p className="attendance-download-subtitle">
            Download a combined attendance report across every class for a single day — optionally
            narrowed to only Present or only Absent students.
          </p>
        </div>
      </div>

      <div className="attendance-download-filters">
        <div className="attendance-download-field">
          <Input
            type="date"
            name="report_date"
            label="Date"
            value={date}
            onChangeHandler={(e) => setDate(e.target.value)}
            required
            max={today}
          />
        </div>
        <div className="attendance-download-field">
          <Select
            name="report_status"
            label="Status"
            placeholder="All statuses"
            value={status}
            onChangeHandler={(e) => setStatus(e.target.value)}
            options={statusOptions}
          />
        </div>
        <div className="attendance-download-action">
          <label className="mb-1 mt-1 fw-semibold d-block invisible">Download</label>
          <Button
            variant="primary"
            icon={<FaDownload />}
            onClick={handleDownload}
            loading={isDownloading}
            disabled={!date}
          >
            Download Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceAllClassesDownload;
